import dayjs from "dayjs"
import { useCallback, useEffect, useRef, useState } from "react"
import {
	Alert,
	Button,
	FormControl,
	OverlayTrigger,
	Popover,
	ProgressBar,
	Tooltip,
} from "react-bootstrap"
import PayRollPickStartDate from "src/components/common/forms/PayRollPickStartDate"
import LDSEllipsis from "src/components/common/LDS/LDSEllipsis/LDSEllipsis"
import { formatForDisplay } from "src/components/vault77/core/MaskedDataField"
import {
	toVault77ApiError,
	Vault77ApiError,
} from "src/components/vault77/core/apiError"
import { shouldAllowAccess } from "src/utilities/helper"
import { getEmployeeReviews } from "src/components/vault77/employeeReviews/api/employeeReview.api"
import type { EmployeeReview } from "src/components/vault77/employeeReviews/types"
import {
	getCurrentPayRate,
	getPayRateHistoryList,
} from "src/components/vault77/payRateHistory/api/payRateHistory.api"
import type { PayRateHistory } from "src/components/vault77/payRateHistory/types"
import PayRateModal from "src/components/vault77/payRateHistory/PayRateModal"
import "./PayAdjuster.css"

// Shown when the employee has no active pay rate. Current pay is derived from the active
// rate, so without one the review cannot be saved — HR must add a rate first.
const NO_PAY_RATE_MESSAGE =
	"This employee has no active pay rate. HR must add a pay rate in the User Admin tab before this review can be saved."

/**
 * The pay fields PayAdjuster owns, named exactly as the Vault77 EmployeeReview API
 * serves and accepts them. This is the shape pushed up to Review, which feeds it
 * straight into the create/update review request — no field remapping, no string or
 * dollar-sign parsing. (This replaces the old Area51 SecureModel/Field1/Field2 indirection.)
 */
export interface PayModel {
	OldPayRate: number | null
	NewPayRate: number | null
	EffectiveDate: string // YYYY-MM-DD
}

export interface PayModelStatus {
	bad: boolean
	oldPayBad: boolean
	newPayBad: boolean
}

interface Props {
	// Unused by PayAdjuster (kept because EditReview passes it); employeeId drives everything.
	userId?: number | null
	// Legacy FieldResponse2_1 EmployeeReviewId — links to the paired Vault77 review via ExternalReviewId.
	reviewId: number
	// ELM EmployeeId (same value Vault77 keys on).
	employeeId: number
	manageParentState: (keys: Array<{ key: string; value?: unknown }>) => void
	readOnly: boolean
	// True once the review has cleared final HR approval (HRApprovedDateUtc set).
	approved?: boolean
}

// Spinner button colors (kept from the original UX).
const SPINNER_MINUS_ACTIVE = "#dc35467c"
const SPINNER_PLUS_ACTIVE = "#28a74681"

// Permission note: these fields edit the PROPOSAL on the Vault77 EmployeeReview (saved via
// Review.upsertVault77Review → POST/PUT /api/vault77/employeeReviews), which the API
// authorizes with Vault77.Write.EmployeeReview. Vault77.Write.PayRate is only exercised
// later, at HR approval, when PayRatePrompt applies the review to PayRateHistory.

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit testing). All money values are plain numbers;
// the column is DECIMAL(10,2) so everything is rounded to two decimals.
// ---------------------------------------------------------------------------

/**
 * Round half-to-even (banker's rounding) to `decimals` places. The epsilon makes a
 * value that is a true half but stored with floating-point error (e.g. 1.005, which is
 * actually 1.00499999…) still count as exactly half, so employee-pay rounding stays
 * unbiased and consistent across the cent boundary.
 */
export function bankersRound(value: number, decimals = 2): number {
	if (!Number.isFinite(value)) {
		return value
	}
	const factor = 10 ** decimals
	const scaled = value * factor
	const floor = Math.floor(scaled)
	const diff = scaled - floor
	const EPSILON = 1e-8
	let result: number
	if (Math.abs(diff - 0.5) < EPSILON) {
		result = floor % 2 === 0 ? floor : floor + 1
	} else {
		result = Math.round(scaled)
	}
	return result / factor
}

/** Percent change from old pay to new pay, rounded to two decimals. */
export function calcPercent(
	oldPay: number | null,
	newPay: number | null,
): number {
	if (newPay == null) {
		return 0
	}
	const a = oldPay ?? 0
	const b = newPay
	let percent: number
	if (b !== 0) {
		percent = a !== 0 ? ((b - a) / a) * 100 : b * 100
	} else {
		// Any pay dropped to 0 is a 100% cut; 0 to 0 is no change.
		percent = a !== 0 ? -100 : 0
	}
	return bankersRound(percent, 2)
}

/** New pay derived from old pay + a percent, rounded to two decimals. */
export function payFromPercent(oldPay: number | null, pct: number): number {
	const base = oldPay ?? 0
	return bankersRound(base + (pct / 100) * base, 2)
}

/** Validation flags: old/new pay must be >= 1 (an empty new pay is allowed). */
export function computeStatus(
	oldPay: number | null,
	newPay: number | null,
): PayModelStatus {
	const oldPayBad = oldPay == null || oldPay < 1
	const newPayBad = newPay != null && newPay < 1
	return { bad: oldPayBad || newPayBad, oldPayBad, newPayBad }
}

/** Compare two pay values at cent precision (the column is DECIMAL(10,2)). */
export const money = (n: number): string => Number(n).toFixed(2)

/**
 * Display a pay value as currency with thousands separators + cents: "$1,234.50".
 * Uses the shared vault77 currency formatter (same one PayRateModal / UserSecure use), so
 * every static pay-rate display in the review/pay UI reads consistently.
 */
export const formatPay = (n: number): string =>
	formatForDisplay(money(n), "currency")

/** Compare two effective dates by calendar day only (they are date-only values). */
export const dateKey = (d?: string | null): string =>
	d ? dayjs(d).format("YYYY-MM-DD") : ""

/**
 * Payroll effective dates fall on 14-day boundaries starting 2005-12-24. Default to the
 * nearest valid boundary so what we persist matches what PayRollPickStartDate shows.
 */
export function getNearestValidDate(): string {
	const minDate = dayjs("2005-12-24")
	const nearestStepCount = Math.round(dayjs().diff(minDate, "day") / 14)
	return minDate.add(nearestStepCount * 14, "day").format("YYYY-MM-DD")
}

/**
 * True when an APPROVED review carries a real pay change that has NO matching
 * PayRateHistory row (nothing links back via SourceReviewId) — the legacy case where a
 * pay change was never recorded in the new pay-rate history (or HR approved without
 * applying it). Surfaced as a fixable warning, not silently.
 */
export function isUnrecordedPayChange(
	approved: boolean | undefined,
	paired: EmployeeReview | null,
	appliedRate: PayRateHistory | null,
): boolean {
	if (!approved || !paired || appliedRate) {
		return false
	}
	if (paired.NewPayRate == null) {
		return false
	}
	return (
		paired.OldPayRate == null ||
		money(paired.NewPayRate) !== money(paired.OldPayRate)
	)
}

export interface LoadedModel {
	oldPayRate: number | null
	newPayRate: number | null
	effectiveDate: string
	currentRateEffectiveDate: string | null
	paired: EmployeeReview | null
	appliedRate: PayRateHistory | null
}

/**
 * Shape the fetched API data into the pay model. Pure so the seeding/diff logic can be
 * unit-tested directly. "Current" pay is ALWAYS the employee's active pay rate
 * (getCurrentPayRate) — it is never authored on the review. If the employee has no active
 * pay rate, current is null, which blocks saving the review (see computeStatus). New pay
 * and the effective date come from the paired review (the proposal).
 */
export function buildLoadModel(params: {
	current: PayRateHistory | null
	reviews: EmployeeReview[] | null
	payRates: PayRateHistory[] | null
	reviewId: number
	fallbackDate: string
	// True once the review has cleared final HR approval. Flips the source of "Current".
	approved?: boolean
}): LoadedModel {
	const { current, reviews, payRates, reviewId, fallbackDate, approved } = params
	let newPayRate: number | null = null
	let effectiveDate = ""

	const paired =
		(reviews || []).find(
			(r) => r.ExternalReviewId === reviewId && !r.DeletedDate,
		) ?? null

	let appliedRate: PayRateHistory | null = null
	if (paired) {
		if (paired.NewPayRate != null) {
			newPayRate = paired.NewPayRate
		}
		effectiveDate = paired.EffectiveDate
			? dayjs(paired.EffectiveDate).format("YYYY-MM-DD")
			: ""
		appliedRate =
			(payRates || []).find(
				(p) => p.SourceReviewId === paired.EmployeeReviewId && !p.DeletedDate,
			) ?? null
	}

	// "Current" (Old) is derived, never user-set, and its source depends on lifecycle:
	//  - Draft / edit (pre-approval): the employee's LIVE active pay rate — you propose the new
	//    rate from what they make today.
	//  - After HR approval (applyReview ran): the review's FROZEN OldPayRate. Reading live here is
	//    wrong — applyReview made the new rate the current rate, so live would show the raise as
	//    the "old" value (Current == New, ~0%). PayRateHistory is truth; the applied review keeps
	//    the snapshot of what they made before.
	// Approved-but-no-frozen-OldPayRate is the legacy/migrated gap — fall back to live, which the
	// "unrecorded pay change" warning already surfaces.
	let oldPayRate: number | null
	let currentRateEffectiveDate: string | null
	if (approved && paired && paired.OldPayRate != null) {
		oldPayRate = paired.OldPayRate
		// The old rate has no stored effective date on the review → no "Effective:" tooltip.
		currentRateEffectiveDate = null
	} else {
		oldPayRate = current && current.PayRate != null ? current.PayRate : null
		currentRateEffectiveDate = current ? (current.EffectiveDate ?? null) : null
	}

	if (!effectiveDate) {
		effectiveDate = fallbackDate
	}

	return {
		oldPayRate,
		newPayRate,
		effectiveDate,
		currentRateEffectiveDate,
		paired,
		appliedRate,
	}
}

/**
 * PayAdjuster — pay-rate editor embedded in an employee review.
 *
 * Old/current pay, new pay, and the effective date are read from the live Vault77 data
 * (current pay rate + the paired EmployeeReview, linked by ExternalReviewId === the
 * legacy review id) and written back to that review on save. The pay is only committed
 * to PayRateHistory at HR approval (PayRatePrompt -> applyReview).
 */
const PayAdjuster: React.FC<Props> = (props) => {
	const { employeeId, reviewId, manageParentState, readOnly, approved } = props

	const [loaded, setLoaded] = useState(false)
	// A load() failure renders an explicit error panel instead of an empty form —
	// otherwise the blank model reads as "employee has no pay rate", which is wrong info.
	const [loadError, setLoadError] = useState(false)
	// A 403 is the server DECLINING, not failing: the viewer may not see this employee's pay
	// (most often their own record, since the hierarchy scope excludes the manager themselves).
	// That is a normal state, so it gets a quiet note rather than the error panel and a Retry
	// that could never succeed. Mirrors how the UserAdmin Secure section handles the same case.
	const [noAccess, setNoAccess] = useState(false)
	const [oldPayRate, setOldPayRate] = useState<number | null>(null)
	const [newPayRate, setNewPayRate] = useState<number | null>(null)
	// The literal text in the Percent field, kept separate from the derived pay so a
	// keystroke is never overwritten by a value re-derived from the rounded new pay.
	const [percentInput, setPercentInput] = useState("")
	const [effectiveDate, setEffectiveDate] = useState("")
	const [currentRateEffectiveDate, setCurrentRateEffectiveDate] = useState<
		string | null
	>(null)
	const [paired, setPaired] = useState<EmployeeReview | null>(null)
	const [appliedRate, setAppliedRate] = useState<PayRateHistory | null>(null)
	const [showPayRateModal, setShowPayRateModal] = useState(false)
	const [payRateModalAutoCreate, setPayRateModalAutoCreate] = useState(false)
	const [activeSpinner, setActiveSpinner] = useState<"-" | "+" | null>(null)

	// Live refs so the press-and-hold spinner steps from the latest values each tick.
	const oldPayRef = useRef(oldPayRate)
	oldPayRef.current = oldPayRate
	const newPayRef = useRef(newPayRate)
	newPayRef.current = newPayRate
	const effRef = useRef(effectiveDate)
	effRef.current = effectiveDate
	const holdTimer = useRef<ReturnType<typeof setInterval> | null>(null)
	// Bumped on every load() call; a fetch only commits its result if it's still the
	// most recent one when it settles, so an older in-flight fetch can't clobber a
	// newer one's data if reviewId/employeeId change (or a manual reload) mid-flight.
	const loadRunId = useRef(0)

	const status = computeStatus(oldPayRate, newPayRate)

	// Push the pay model + status up to Review. `modified` flags a user edit so the Save
	// button enables and updateReview() upserts the Vault77 review.
	const pushModel = useCallback(
		(
			old: number | null,
			neu: number | null,
			eff: string,
			modified: boolean,
		) => {
			const keys: Array<{ key: string; value?: unknown }> = [
				{
					key: "payModel",
					value: {
						OldPayRate: old,
						NewPayRate: neu,
						EffectiveDate: eff,
					} as PayModel,
				},
				{ key: "modelStatus", value: computeStatus(old, neu) },
			]
			if (modified) {
				keys.push({ key: "modifiedPay", value: true })
			}
			manageParentState(keys)
		},
		[manageParentState],
	)

	const load = useCallback(async () => {
		const runId = ++loadRunId.current
		setLoaded(false)
		setLoadError(false)
		setNoAccess(false)
		let result: LoadedModel
		let failed = false
		let declined = false
		try {
			const [current, reviews, payRates] = await Promise.all([
				getCurrentPayRate(employeeId),
				getEmployeeReviews(employeeId),
				getPayRateHistoryList({ employeeId }),
			])
			result = buildLoadModel({
				current,
				reviews,
				payRates,
				reviewId,
				fallbackDate: getNearestValidDate(),
				approved,
			})
		} catch (err) {
			// The vault77 api layer already converts to Vault77ApiError before throwing, so the
			// caught value usually carries .status directly and has no .response to duck-type.
			// Convert only when it is something else (a raw axios error, say).
			const apiError =
				err instanceof Vault77ApiError ? err : toVault77ApiError(err, "")
			declined = apiError.status === 403
			if (!declined) {
				console.error("PayAdjuster load error:", err)
			}
			failed = true
			// The empty model still seeds the parent so its status is bad and Save stays
			// disabled either way; the render swaps the form for the error panel (a real
			// failure) or the quiet no-access note (a 403) below.
			result = buildLoadModel({
				current: null,
				reviews: null,
				payRates: null,
				reviewId,
				fallbackDate: getNearestValidDate(),
				approved,
			})
		}

		if (runId !== loadRunId.current) {
			// A newer load() started while this one was in flight — its result is stale, drop it.
			return
		}

		setOldPayRate(result.oldPayRate)
		setNewPayRate(result.newPayRate)
		setEffectiveDate(result.effectiveDate)
		setCurrentRateEffectiveDate(result.currentRateEffectiveDate)
		setPaired(result.paired)
		setAppliedRate(result.appliedRate)
		const pct = calcPercent(result.oldPayRate, result.newPayRate)
		setPercentInput(result.newPayRate != null ? `${pct}` : "")
		setNoAccess(declined)
		setLoadError(failed && !declined)
		setLoaded(true)

		if (result.paired) {
			manageParentState([
				{ key: "vault77ReviewId", value: result.paired.EmployeeReviewId },
			])
		}
		// Seed the parent (load is not a user edit, so modifiedPay stays false).
		pushModel(result.oldPayRate, result.newPayRate, result.effectiveDate, false)
	}, [employeeId, reviewId, approved, manageParentState, pushModel])

	useEffect(() => {
		load()
	}, [load])

	// Clean up the spinner interval on unmount.
	useEffect(
		() => () => {
			if (holdTimer.current) {
				clearInterval(holdTimer.current)
			}
		},
		[],
	)

	const onNewPayChange = (raw: string) => {
		const parsed = raw === "" ? null : Number(raw)
		const next =
			parsed != null && Number.isFinite(parsed) ? bankersRound(parsed, 2) : null
		setNewPayRate(next)
		setPercentInput(next != null ? `${calcPercent(oldPayRate, next)}` : "")
		pushModel(oldPayRate, next, effectiveDate, true)
	}

	// Typing in the Percent field: keep exactly what was typed, derive new pay from it.
	const onPercentText = (raw: string) => {
		setPercentInput(raw)
		if (status.bad) {
			return
		}
		const value = Number(raw)
		if (raw.trim() === "" || !Number.isFinite(value)) {
			setNewPayRate(null)
			pushModel(oldPayRate, null, effectiveDate, true)
		} else {
			const neu = payFromPercent(oldPayRate, value)
			setNewPayRate(neu)
			pushModel(oldPayRate, neu, effectiveDate, true)
		}
	}

	// Spinner step: nudge the percent and reflect the derived pay + stepped percent.
	const stepPercent = (direction: "-" | "+") => {
		const old = oldPayRef.current
		const basePct = calcPercent(old, newPayRef.current)
		const stepSize = (old ?? 0) <= 5 ? 1 : 0.1
		const nextPct = bankersRound(
			direction === "-" ? basePct - stepSize : basePct + stepSize,
			2,
		)
		const neu = payFromPercent(old, nextPct)
		newPayRef.current = neu
		setNewPayRate(neu)
		setPercentInput(`${nextPct}`)
		pushModel(old, neu, effRef.current, true)
	}

	const startHold = (direction: "-" | "+") => {
		if (readOnly || status.bad || !shouldAllowAccess("Vault77.Write.EmployeeReview")) {
			return
		}
		setActiveSpinner(direction)
		stepPercent(direction)
		holdTimer.current = setInterval(() => stepPercent(direction), 500)
	}

	const stopHold = () => {
		setActiveSpinner(null)
		if (holdTimer.current) {
			clearInterval(holdTimer.current)
			holdTimer.current = null
		}
	}

	const openPayRateModalForFix = () => {
		setPayRateModalAutoCreate(true)
		setShowPayRateModal(true)
	}

	const openPayRateHistory = () => {
		setPayRateModalAutoCreate(false)
		setShowPayRateModal(true)
	}

	// Close only hides the modal — it does NOT re-fetch. Any pay-rate change made inside the
	// modal already refreshes PayAdjuster via onChanged (create/edit/link/delete all call it),
	// so reloading here too would be redundant on a change and a wasted fetch (+ spinner flash)
	// when the modal was opened just to view history.
	const closePayRateModal = () => {
		setShowPayRateModal(false)
	}

	// A warning icon + popover shown when a proposed field on the review differs from the
	// pay-rate row it was applied to. PayRateHistory is the source of truth; the review
	// value is the (frozen) proposal, so this surfaces a later HR edit to the applied rate.
	const renderDiffWarning = (
		label: string,
		proposed: string,
		actual: string,
	) => (
		<OverlayTrigger
			placement="top"
			overlay={
				<Popover id={`pay-adjuster-diff-${label}`}>
					<Popover.Title as="h3">{`${label} changed after approval`}</Popover.Title>
					<Popover.Content>
						<div className="PayAdjusterDiffRow">
							<span>Proposed:</span>
							<b>{proposed}</b>
						</div>
						<div className="PayAdjusterDiffRow">
							<span>Actual:</span>
							<b>{actual}</b>
						</div>
						<p className="PayAdjusterDiffNote">
							The applied pay rate is the source of truth.
						</p>
					</Popover.Content>
				</Popover>
			}
		>
			<span className="material-icons PayAdjusterDiffWarning">warning</span>
		</OverlayTrigger>
	)

	const renderUnrecordedWarning = () => (
		<OverlayTrigger
			placement="top"
			overlay={
				<Tooltip id="pay-adjuster-unrecorded">
					This approved pay change isn't recorded in pay rate history. Click to
					open pay rate history and record it.
				</Tooltip>
			}
		>
			<span
				className="material-icons PayAdjusterUnrecordedWarning"
				onClick={openPayRateModalForFix}
			>
				report_problem
			</span>
		</OverlayTrigger>
	)

	const renderViewHistoryButton = () => (
		<OverlayTrigger
			placement="top"
			overlay={
				<Tooltip id="pay-adjuster-view-history">View pay rate history</Tooltip>
			}
		>
			<span
				className="material-icons PayAdjusterHistoryButton"
				onClick={openPayRateHistory}
			>
				history
			</span>
		</OverlayTrigger>
	)

	const payAdjusterTooltip = (text: string) => (
		<Tooltip id="pay-adjuster-tool-tip">{text}</Tooltip>
	)

	const percent = calcPercent(oldPayRate, newPayRate)
	const up = percent > 0
	const timeFormat = "MM/DD/YYYY"

	// Proposed (review) vs actual (applied PayRateHistory row) drift. Only meaningful once
	// the review has been applied; pre-approval there is no applied row.
	const payDiff =
		appliedRate != null &&
		paired != null &&
		paired.NewPayRate != null &&
		appliedRate.PayRate != null &&
		money(paired.NewPayRate) !== money(appliedRate.PayRate)
	const dateDiff =
		appliedRate != null &&
		paired != null &&
		dateKey(paired.EffectiveDate) !== dateKey(appliedRate.EffectiveDate)
	const payProposed =
		paired && paired.NewPayRate != null ? formatPay(paired.NewPayRate) : ""
	const payActual =
		appliedRate && appliedRate.PayRate != null
			? formatPay(appliedRate.PayRate)
			: ""
	const dateProposed = paired
		? dayjs(paired.EffectiveDate).format(timeFormat)
		: ""
	const dateActual = appliedRate
		? dayjs(appliedRate.EffectiveDate).format(timeFormat)
		: ""
	const unrecorded = isUnrecordedPayChange(approved, paired, appliedRate)

	return (
		<div
			className={`PayAdjuster ${readOnly ? "PayAdjusterReadOnly" : "PayAdjusterNotReadOnly"}`}
			id="pay-adjuster"
		>
			{showPayRateModal && (
				<PayRateModal
					employeeId={employeeId}
					sourceReviewId={paired ? paired.EmployeeReviewId : null}
					prefill={{
						PayRate: newPayRate != null ? `${newPayRate}` : "",
						EffectiveDate: effectiveDate
							? dayjs(effectiveDate).format("YYYY-MM-DD")
							: "",
					}}
					autoCreateWhenEmpty={payRateModalAutoCreate}
					onChanged={() => load()}
					onClose={closePayRateModal}
				/>
			)}
			{!loaded && (
				<div className="PayAdjusterLoadingModel">
					<LDSEllipsis />
					<p>Loading pay details</p>
				</div>
			)}
			{loaded && noAccess && (
				<div className="PayAdjusterMessage">
					<p className="PayAdjusterNoAccess">
						Pay details for this employee aren't available to you.
					</p>
				</div>
			)}
			{loaded && loadError && (
				<div className="PayAdjusterMessage">
					<Alert variant="danger" className="PayAdjusterLoadError">
						Couldn't load pay details. The review can't be saved until they load.
					</Alert>
					<Button
						id="pay-adjuster-load-retry"
						variant="secondary"
						onClick={() => load()}
					>
						Retry
					</Button>
				</div>
			)}
			{loaded && !loadError && !noAccess && (
				<div className="PayAdjusterForm">
					{oldPayRate == null && (
						<Alert variant="warning" className="PayAdjusterNoPayRate">
							{NO_PAY_RATE_MESSAGE}
						</Alert>
					)}
					{/* Current pay — read-only, always derived from the active pay rate */}
					<div className="PayAdjusterFormItem">
						<div className="PayAdjusterFormItemHeader">
							<p>Current</p>
							{renderViewHistoryButton()}
							{status.oldPayBad && (
								<span className="material-icons">priority_high</span>
							)}
						</div>
						<div className="PayAdjusterFormItemContent">
							<FormControl
								value={oldPayRate != null ? formatPay(oldPayRate) : "*NA*"}
								type="text"
								readOnly
								disabled
							/>
							{currentRateEffectiveDate && (
								<div className="PayAdjusterFormItemOverlay">
									<OverlayTrigger
										delay={{ show: 0, hide: 0 }}
										placement="top"
										overlay={payAdjusterTooltip(
											`Effective: ${dayjs(currentRateEffectiveDate).format(timeFormat)}`,
										)}
									>
										<span className="material-icons">event</span>
									</OverlayTrigger>
								</div>
							)}
						</div>
					</div>
					{/* New pay */}
					<div className="PayAdjusterFormItem">
						<div className="PayAdjusterFormItemHeader">
							<p>New</p>
							{payDiff && renderDiffWarning("New pay", payProposed, payActual)}
							{unrecorded && renderUnrecordedWarning()}
							{status.newPayBad && (
								<span className="material-icons">priority_high</span>
							)}
						</div>
						<div className="PayAdjusterFormItemContent">
							{readOnly ? (
								// Locked in (read-only): show the proposed rate as static currency,
								// formatted like Current. A number input can't render "$"/commas.
								<FormControl
									value={newPayRate != null ? formatPay(newPayRate) : "*NA*"}
									type="text"
									readOnly
									disabled
								/>
							) : (
								<FormControl
									id="pay-adjuster-new-pay-input"
									onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
										onNewPayChange(e.target.value)
									}
									value={newPayRate ?? ""}
									placeholder="..."
									type="number"
									disabled={!shouldAllowAccess("Vault77.Write.EmployeeReview")}
								/>
							)}
						</div>
					</div>
					{/* Percent */}
					<div className="PayAdjusterFormItem">
						<div className="PayAdjusterFormItemHeader PayAdjusterFormItemHeaderNumberSpinnerHeader">
							<p>Percent</p>
							{!status.bad && (
								<ProgressBar
									id="pay-adjuster-number-spinner-progress-bar"
									now={up ? percent : percent * -1}
									variant={up ? "success" : "danger"}
								/>
							)}
						</div>
						<div className="PayAdjusterFormItemContent">
							<div className="PayAdjusterFormItemNumberSpinner">
								<div
									className="PayAdjusterFormItemNumberSpinnerBtn PayAdjusterFormItemNumberSpinnerBtnLeft"
									id="pay-adjuster-number-spinner-btn-left"
									style={
										activeSpinner === "-"
											? { backgroundColor: SPINNER_MINUS_ACTIVE }
											: undefined
									}
									onMouseDown={() => startHold("-")}
									onMouseUp={stopHold}
									onMouseLeave={stopHold}
								>
									<span className="material-icons">remove</span>
								</div>
								<div className="PayAdjusterFormItemNumberSpinnerValue">
									<FormControl
										id="pay-adjuster-new-pay-percent-input"
										onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
											onPercentText(e.target.value)
										}
										placeholder="0"
										value={percentInput}
										type="text"
										inputMode="decimal"
										readOnly={readOnly || status.bad || !shouldAllowAccess("Vault77.Write.EmployeeReview")}
									/>
								</div>
								<div
									className="PayAdjusterFormItemNumberSpinnerBtn PayAdjusterFormItemNumberSpinnerBtnRight"
									id="pay-adjuster-number-spinner-btn-right"
									style={
										activeSpinner === "+"
											? { backgroundColor: SPINNER_PLUS_ACTIVE }
											: undefined
									}
									onMouseDown={() => startHold("+")}
									onMouseUp={stopHold}
									onMouseLeave={stopHold}
								>
									<span className="material-icons">add</span>
								</div>
							</div>
						</div>
					</div>
					{/* Effective date */}
					<PayRollPickStartDate
						value={effectiveDate}
						onChange={(value) => {
							const next = value ?? ""
							setEffectiveDate(next)
							pushModel(oldPayRate, newPayRate, next, true)
						}}
						readOnly={readOnly || !shouldAllowAccess("Vault77.Write.EmployeeReview")}
						label="Effective Date"
						headerAccessory={
							dateDiff
								? renderDiffWarning("Effective date", dateProposed, dateActual)
								: undefined
						}
					/>
				</div>
			)}
		</div>
	)
}

export default PayAdjuster
